import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { LatestOrder } from '@domains/main-page';

import { makeOrderHistoryRoute } from '@utils/route';

import { Container } from '@components/common/Container/Container';
import { Link } from '@components/common/Link/Link';
import { Picture } from '@components/common/Picture/Picture';
import { LinkArrowIcon } from '@components/icons';

import styles from './LastOrder.module.scss';

type LastOrderProps = LatestOrder;
export const LastOrder: FunctionComponent<LastOrderProps> = (props) => {
    const { several_items, count } = props;

    const severalItemsCount = several_items.length;

    // On a mobile device, the layout of the block may break before calling useEffect if you specify more than 1
    const [visibilityCount, setVisibilityCount] = useState(1);

    const setMinVisibilityCount = useCallback(
        (viewCount: number) => setVisibilityCount(Math.min(viewCount, severalItemsCount)),
        [severalItemsCount],
    );

    const visibilityProducts = useMemo(
        () => several_items.slice(0, visibilityCount),
        [several_items, visibilityCount],
    );

    const remainderProducts = useMemo(() => count - visibilityCount, [count, visibilityCount]);

    const resizeHandler = useCallback(() => {
        const windowWidth = window.innerWidth;

        switch (true) {
            case windowWidth >= 1060 && windowWidth < 1300: {
                setMinVisibilityCount(4);
                break;
            }
            case windowWidth >= 960 && windowWidth < 1060: {
                setMinVisibilityCount(3);
                break;
            }
            case windowWidth >= 360 && windowWidth < 960: {
                setMinVisibilityCount(2);
                break;
            }
            case windowWidth < 360: {
                setMinVisibilityCount(1);
                break;
            }
            default: {
                setMinVisibilityCount(6);
                break;
            }
        }
    }, [setMinVisibilityCount]);

    useEffect(() => {
        resizeHandler();
        window.addEventListener('resize', resizeHandler);
        return () => window.removeEventListener('resize', resizeHandler);
    }, [resizeHandler]);

    return (
        <div className={styles.root}>
            <Container classes={{ root: styles.container }}>
                <div className={styles.content}>
                    <div className={styles.info}>
                        <h3 className={styles.title}>Ваш прошлый заказ</h3>
                        <Link route={makeOrderHistoryRoute()} className={styles.link}>
                            <span>Повторить</span>
                            <LinkArrowIcon className={styles.icon} />
                        </Link>
                    </div>

                    <div className={styles.products}>
                        {visibilityProducts.map(({ product_image }, index) => (
                            <div className={styles.product} key={index}>
                                <Picture sources={product_image} className={styles.image} />
                            </div>
                        ))}

                        {remainderProducts > 0 && (
                            <div className={clsx(styles.remainder, styles.product)}>
                                <span className={styles.remainderText}>+{remainderProducts}</span>
                            </div>
                        )}
                    </div>
                </div>
            </Container>
        </div>
    );
};
